// META TAGS
// alwaysAllowed - only used for authentication routes since they are normaly only available when user is not authenticated, it overrides the notAuth only behaviour
// notRequiredAuth - used for routes that usually require authentication but can be accessed without it
// requireProject - only valid for Group view, set if module can only operate on the project scope
// permission - define a list of permissions necessary to access the route
// breadcrumbs: false - set if the route should not be included in the breadcrumbs
import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'

import store from '@/store'

import dashboardsRoutes from '@/views/Project/Dashboard/router/dashboard'
import notificationRoutes from '@/views/Notification/router/notification'
import { contractorsRoute } from '@/views/Project/ch/contractorsRoute'

import projectUsers from '@/views/Project/ProjectUsers/router/project-users'
import observationsRoutes from '@/views/Project/Observation/router/observation'
import settingsRoutes from '@/views/Project/Settings/router/settings-router'

import languageRoutes from '@/views/Language/router/language'

// Modules
import authenticationRoutes from '@/views/Authentication/router/authentication'
import userRoutes from '@/views/User/routers/user'

import hseRoutes from '@/views/Project/HSE/router/hse'
import bklrRoutes from '@/views/Project/Bklr/router/bklr'
import accessRoutes from '@/views/Project/access/routes'
import personnelRoutes from '@/views/Project/Personnel/router/personnel'

import PSIRoutes from '@/views/Project/PSI/router/psi'
import contractorRespondRoute from '@/views/respond/ch/routes'
import { courseRoutes } from '@/views/Project/course/routes'

Vue.use(VueRouter)

const routes: Array<RouteConfig> = [
    contractorRespondRoute,
    authenticationRoutes,
    languageRoutes,
    {
        path: '/:type(p|g)/:id(\\d+)',
        name: 'project',
        component: () => import('../views/Project/Project.vue'),
        children: [
            ...hseRoutes,
            ...bklrRoutes,
            ...personnelRoutes,
            dashboardsRoutes,
            notificationRoutes,
            observationsRoutes,
            settingsRoutes,
            projectUsers,
            ...contractorsRoute,
            ...courseRoutes,
            ...accessRoutes,
            PSIRoutes,
        ],
    },

    userRoutes,
    {
        path: '/a/p/:id(\\d+)',
        name: 'anonymous-project',
        meta: { title: 'project_menu.observation', notRequiredAuth: true },
        component: () => import('@/views/AnonymousProject/views/Index.vue'),
        children: [
            {
                path: 'o',
                name: 'add-anonymous-observation',
                component: () => import('@/views/AnonymousProject/views/Observation.vue'),
            },
        ],
    },
]

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
})

async function checkForUpdates() {
    if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
        let registration = await navigator.serviceWorker.getRegistration()
        if (
            registration &&
            registration.waiting !== null &&
            registration.waiting.state === 'installed'
        ) {
            registration.waiting?.postMessage({ type: 'SKIP_WAITING' })
        }
    }
}

function handleLegacyCode(fullPath: string) {
    return fullPath
        .replace(/^\/user\/\d+\//, '/u/')
        .replace(/\/user-settings/, '/s')

        .replace(/\/general/, '/g')
        .replace(/\/contact/, '/c')
        .replace(/\/notifications/, '/n')
        .replace(/\/notification/, '/n')
        .replace(/\/superuser/, '/su')

        .replace(/\/project/, '/p')
        .replace(/\/group/, '/g')

        .replace(/\/dashboard/, '/d')
        .replace(/\/create-dashboard/, '/')
        .replace(/\/add-widget/, '/w')

        .replace(/\/users/, '/u')

        .replace(/\/settings/, '/s')
        .replace(/\/hse-settings/, '/hse')

        .replace(/\/locations/, '/l')
        .replace(/\/observations-settings/, '/o')
        .replace(/\/general-observations-settings/, '/g')
        .replace(/\/categories-observations-settings/, '/cat')
        .replace(/\/causes-observations-settings/, '/cau')

        .replace(/\/users-settings/, '/u')
        .replace(/\/roles-setting/, '/r')
        .replace(/\/modules/, '/m')
}

router.beforeEach(async (to, from, next) => {
    const isAuthenticated = store.getters.authenticated

    //At the moment available top level routes: authentication, user, project, language, respond
    const topLevelRoute = to.matched[0]?.name

    // Service worker triggered
    // if (to.matched[0]?.name == 'projects-list' || from.matched[0]?.name) {
    //     await checkForUpdates()
    // }

    // LEGACY ROUTES
    // In first version of the app we had long route names, they were changed to shorter ones
    // If we can't find top level route name it might be a legacy route
    // To support the old urls check if the route name is not found
    const isLegacyRoute = !topLevelRoute
    // If it's not found, try to format the fullPath url to the new format
    if (isLegacyRoute) {
        const newPath = handleLegacyCode(to.fullPath)
        if (newPath != to.fullPath) next(newPath)
        else isAuthenticated ? next({ name: 'user' }) : next({ name: 'sign-in' })
    } else {
        // Some modules in the group view require projectId to show information correctly
        // In order to set the project id when need to check:
        // 1. If the route is a group route
        // 2.That this view requires a project id: meta.requireProject: true
        // 3. That route is missing projectId and that we have a currentProjectId in the store
        if (
            to.name &&
            to.params.type == 'g' &&
            to.meta.requireProject &&
            !to.params.projectId &&
            store.getters.currentProjectId
        ) {
            next({ name: to.name, params: { projectId: store.getters.currentProjectId } })
            return
        }

        // Authentication routes are special, they are only available when user is not authenticated unless alwaysAllowed is set

        if (topLevelRoute == 'authentication') {
            const alwaysAllowed = to.matched.findIndex((item) => item.meta['alwaysAllowed']) > -1

            if (alwaysAllowed) {
                next()
                return
            } else {
                if (isAuthenticated) {
                    next(to.query.url ? `${to.query.url}` : { name: 'user' })
                    return
                } else {
                    next()
                    return
                }
            }
        } else {
            // All other routes are only available when user is authenticated unless notRequiredAuth is set
            const notRequiredAuth =
                to.matched.findIndex((item) => item.meta['notRequiredAuth']) > -1
            if (notRequiredAuth) {
                next()
                return
            } else {
                if (isAuthenticated) {
                    next()
                    return
                } else {
                    next({ name: 'sign-in', query: { url: to.fullPath } })
                    return
                }
            }
        }
    }
})

export default router

declare module 'vue/types/vue' {
    interface Vue {
        $router: VueRouter
    }
}
